import { useMemo } from 'react';
import { useMatches, type UIMatch } from 'react-router';
import type { ShopperProducts } from '@/scapi';

/** PLP loaders return `category` eagerly; PDP defers it as a Promise. */
export function isEagerCategory(category: unknown): category is ShopperProducts.schemas['Category'] {
    return typeof category === 'object' && category !== null && !('then' in category);
}

/** Subset of `_app.c.$` loader data used to locate the active PLP route match. */
export type PlpRouteLoaderData = {
    categoryId: string;
    category: ShopperProducts.schemas['Category'];
};

export function isPlpRouteLoaderData(data: unknown): data is PlpRouteLoaderData {
    if (typeof data !== 'object' || data === null) {
        return false;
    }

    const record = data as PlpRouteLoaderData;
    return typeof record.categoryId === 'string' && isEagerCategory(record.category);
}

/**
 * Finds the deepest active route match whose loader is the category PLP (`_app.c.$`).
 * Works regardless of locale route ID suffixes (e.g. `--locale-en`).
 */
export function findPlpRouteMatch<T = unknown>(matches: UIMatch<T>[]): UIMatch<T> | undefined {
    return [...matches].reverse().find((match) => isPlpRouteLoaderData(match.loaderData));
}

/**
 * Returns the resolved category from the active PLP route loader, if any.
 * Use in Page Designer PLP components (e.g. The Selection) and for optimistic PLP breadcrumb updates.
 */
export function usePlpRouteCategory(): ShopperProducts.schemas['Category'] | undefined {
    const matches = useMatches();

    return useMemo(() => {
        const match = findPlpRouteMatch(matches);
        return match ? (match.loaderData as PlpRouteLoaderData).category : undefined;
    }, [matches]);
}
